import { useEffect, useRef, useState } from 'react';
import { Resizable } from 're-resizable';
import { useQuery } from '@tanstack/react-query';
import { OverlayPanel } from 'primereact/overlaypanel';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import { useNavigate } from 'react-router-dom';
import { useCompanyContext } from '../../../context/CompanyContext';
import WrapperButton from '../../../components/buttons/WrapperButton';
import DropdownStyled from '../../../components/inputs/StyledInputs/DropdownStyled';
import { formatUTCDate } from '@/src/utils/formatUtils';
import {
  PendingActionItem,
  PendingActionSSEEvent,
} from '../../../Interfaces/system/PendingActions.interfaces';
import {
  fetchPendingActions,
  openPendingActionsStream,
} from '../../../services/system/PendingActionsService';
import useVisibleProcessOptions from '../../../hooks/useVisibleProcessOptions';
import { PENDING_ACTION_PROCESS_OPTIONS } from '../../../../utils/rolesConst';
import BuildingIcon from '../../../../assets/layout/images/icons/building-solid.svg';

const PendingActions = () => {
  const overlayRef = useRef<OverlayPanel>(null);
  const streamRef = useRef<AbortController | null>(null);
  const generationRef = useRef(0);
  const navigate = useNavigate();
  const { selectedCompany } = useCompanyContext();
  const [panelSize, setPanelSize] = useState({ width: 352, height: 480 });
  const [companyFilter, setCompanyFilter] = useState(false);
  const [processFilter, setProcessFilter] = useState<string | undefined>(
    undefined
  );
  const [items, setItems] = useState<PendingActionItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const filtersRef = useRef({ process: processFilter, companyFilter });

  const processOptions = useVisibleProcessOptions(
    PENDING_ACTION_PROCESS_OPTIONS
  );

  const { isFetching, refetch } = useQuery({
    queryKey: [
      'pendingActions',
      companyFilter ? selectedCompany?.id : undefined,
      processFilter,
    ],
    queryFn: () =>
      fetchPendingActions(
        companyFilter ? selectedCompany?.id : undefined,
        processFilter
      ),
    enabled: true,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      setItems(data);
      if (!processFilter && !companyFilter) {
        setTotalCount(data.length);
      }
    },
  });

  useEffect(() => {
    filtersRef.current = { process: processFilter, companyFilter };
  }, [processFilter, companyFilter]);

  useEffect(() => {
    if (!selectedCompany?.id) return;
    const myGeneration = ++generationRef.current;

    const startStream = () => {
      streamRef.current?.abort();
      streamRef.current = openPendingActionsStream((incoming) => {
        if (generationRef.current !== myGeneration) return;
        const event = incoming as PendingActionSSEEvent;

        const { process: activeProcess, companyFilter: activeCompanyFilter } =
          filtersRef.current;

        if (event.action === 'add') {
          setTotalCount((c) => c + 1);

          if (
            activeCompanyFilter &&
            event.pendingAction.companyId !== selectedCompany?.id
          )
            return;

          if (activeProcess && event.pendingAction.process !== activeProcess)
            return;
          setItems((prev) =>
            prev.some((i) => i.id === event.pendingAction.id)
              ? prev
              : [event.pendingAction, ...prev]
          );
        } else if (event.action === 'remove') {
          setTotalCount((c) => Math.max(0, c - 1));
          setItems((prev) => prev.filter((i) => i.id !== event.id));
        } else if (event.action === 'sync') {
          if (activeProcess !== undefined || activeCompanyFilter) {
            setProcessFilter(undefined);
            setCompanyFilter(false);
          } else {
            refetch();
          }
        }
      });
    };

    startStream();

    return () => {
      generationRef.current = myGeneration + 1;
      streamRef.current?.abort();
    };
  }, [selectedCompany?.id, refetch]);

  const handleItemClick = (redirectionLink: string) => {
    navigate(redirectionLink);
    overlayRef.current?.hide();
  };

  return (
    <>
      <WrapperButton
        onClick={(e) => overlayRef.current?.toggle(e)}
        className="relative"
      >
        <i
          className={`pi pi-bolt m-2 text-32px font-bold ${
            totalCount > 0 ? 'text-red-500' : 'text-green-500'
          }`}
          style={{ lineHeight: '1' }}
        />
      </WrapperButton>

      <OverlayPanel
        ref={overlayRef}
        className="overlayNoPadding overlayNoArrow"
      >
        <Resizable
          size={panelSize}
          onResizeStop={(_e, _direction, _ref, delta) =>
            setPanelSize((prev) => ({
              width: prev.width + delta.width,
              height: prev.height + delta.height,
            }))
          }
          minWidth={370}
          minHeight={300}
          maxWidth={800}
          enable={{ right: true, bottom: true, bottomRight: true }}
          style={{ overflow: 'hidden' }}
          className="p-3"
        >
          <div className="flex align-items-center mb-3 blackText">
            <span className="font-semibold text-lg">Pending Actions</span>
          </div>

          <div className="flex align-items-center gap-2 mb-3">
            <Tooltip
              target=".pa-company-filter-btn"
              content="Current company"
              position="top"
            />
            <button
              className={`pa-company-filter-btn border-round border-1 cursor-pointer ${
                companyFilter
                  ? 'bluwaiBlue border-primary'
                  : 'surface-0 border-300'
              } p-2`}
              onClick={() => setCompanyFilter((v) => !v)}
            >
              <img
                src={BuildingIcon}
                alt="company"
                className={`h-1rem w-1rem ${companyFilter ? 'svgWhite' : ''}`}
              />
            </button>
            <DropdownStyled
              options={processOptions}
              labelField="label"
              valueField="value"
              value={processFilter}
              onChange={(e) => setProcessFilter(e.value ?? undefined)}
              placeholder="All processes"
              className="flex-1"
              fontClass="text-sm"
              showClear
            />
          </div>

          <div
            className="overflow-y-auto"
            style={{ height: `${panelSize.height - 145}px` }}
          >
            {isFetching && (
              <ProgressSpinner
                style={{ width: '2rem', height: '2rem' }}
                className="flex justify-content-center py-2"
              />
            )}

            {!isFetching && items.length === 0 && (
              <div className="p-3 text-color-secondary text-sm text-center">
                No pending actions
              </div>
            )}

            {!isFetching &&
              items.map((item) => (
                <div
                  key={item.id}
                  onClick={() => handleItemClick(item.redirectionLink)}
                  className="mx-1 mb-3 flex flex-column gap-1 blackText p-2 border-round cursor-pointer hover:surface-hover shadow-2"
                >
                  <span className="font-medium text-sm">{item.title}</span>
                  <span className="text-color-secondary text-xs">
                    {item.content}
                  </span>
                  <span className="text-color-secondary text-xs mt-2">
                    <span className="font-bold mr-1">Submitted By:</span>
                    {item.submittedBy.userName}
                  </span>
                  <span className="text-color-secondary text-xs ml-auto">
                    {formatUTCDate(item.createdAt)}
                  </span>
                </div>
              ))}
          </div>
        </Resizable>
      </OverlayPanel>
    </>
  );
};

export default PendingActions;
